import { PSBErrors } from "@/constants/errors/product-slot-booking";
import {
  AdyenResponseStatus,
  IAdyenResponseStatusTrack,
  IBookingDiscount,
  IBookingDiscountTrack,
  IBookingPayment,
  IBookingPaymentTrack,
  IBookingsPendingSync,
  IBookingsSteps,
  IBookingState,
  IConfirmedAmount,
  IPromocodeTrack,
  SyncType,
  BookingChange,
  BookingChangesTrack,
  RefundListenerTrack,
} from "@/interfaces/store/IBooking";
import {
  IBookingCustomerInfo,
  IProductSlotBooking,
  BaseOrderSession,
} from "@/types/db/product-slot-booking";
import { IRemovePSS, PaymentStatus } from "@/types/db/product-slot-state";
import { getPSSValue } from "@/utils/helpers/product-slot-state";
import {
  getModule,
  VuexModule,
  Mutation,
  Action,
  Module,
} from "vuex-module-decorators";
import store from "..";
import { BookingStep } from "@/constants/booking";
import {
  AccountingState,
  IAccounting,
  IAccountingInfo,
  PaymentCard,
  PaymentMethods,
  TransactionTypes,
} from "@/types/db/accounting";
import { Currencies, Currency } from "@/constants/currency";
import { generateID } from "@/utils/helpers/app";
import { bookings, maxBookingTabsToShow } from "@/utils/vue-helpers/booking";
import { LogLevel, LogService } from "@/services/logging";
import dayjs from "dayjs";
import { roundToNearestMultipleOf5, sumArray } from "@/utils/helpers/common";
import { OrderSessionState } from "@smeetz/order-common";
import { PaymentMethod } from "@smeetz/orders";
import {
  processAdditionalEvents,
  // processStartSaleEvent,
} from "@/utils/helpers/compliance";
import { EventType } from "@smeetz/compliance-common";
import {
  IBookingWaiverTrack,
  ISignedWaiver,
} from "@/interfaces/store/IBooking";

@Module({ dynamic: true, namespaced: true, store, name: "Booking" })
class BookingStoreModule extends VuexModule implements IBookingState {
  public bookings: IBookingState["bookings"] = {};
  public bookingList: IBookingState["bookingList"] = [];
  public isTabAnimated = false;
  // public bookingStep: BookingStep = BookingStep.Order;
  public bookingsSteps: IBookingsSteps = {};
  // public isLoading = false;
  public selectedBookingUUID = "";
  public bookingsAmountsTrack: IBookingPaymentTrack = {};
  public bookingsDiscountsTrack: IBookingDiscountTrack = {};
  // public payingCurrency = Currencies.CHF;
  // The below array keeps the relationship between a promocode and a booking, used to calculate the discount for the UI and for the moment we create accountings
  // We add one item when a promocode is valid for a given booking
  // We remove one item when the booking is completed/cancelled
  public promocodes: IPromocodeTrack[] = [];
  // The below will contian pending bookings synched to backend, they are copies of what backend has. We will need them to build Patch synchronize-bookings payload
  // the whole scenario is needed when a booking has a promocode applied or when we're editing an order that we pulled from the backend
  //
  public adyenResponseStatusTrack: IAdyenResponseStatusTrack = {};

  public bookingsPendingSynchronization: IBookingsPendingSync = {};

  public calculatorValue = "0";

  public selectedCashCurrency: Currency | null = null;

  public currentOrderSessionId: string | null = null;

  public recentTabNames: string[] = [];

  public currentBookingCount = 0;

  public promocodeBeingChecked = false;

  public refundListeners: RefundListenerTrack = {};

  /**
   * Ticket removals / quantity decreases per booking, reported to the Belgian
   * FDM in the sale event. Only written when Belgian compliance is active.
   */
  public bookingChanges: BookingChangesTrack = {};

  logGettersPerformance = false;

  public bookingsWaiversTrack: { [bookingUUID: string]: IBookingWaiverTrack } =
    {};

  get selectedBookingLinkedWaivers(): ISignedWaiver[] {
    return (
      this.bookingsWaiversTrack[this.selectedBookingUUID]?.linkedWaivers || []
    );
  }

  /**
   * Whether or not the current payment supports split payment
   * For now, we accept split payment when editing an order
   * You can add more cases to the return statement
   */
  get doesSupportSplitPayment() {
    return true;
  }

  /**
   * Returns {splitPaymentAmount} split amount supplied by the user, rounded to a multiple of 5 if main currency is CHF
   * and {splitAmountProvided} which the same amount without any rounding operations
   */
  get splitAmount() {
    const dt1 = new Date();
    if (this.bookingsAmountsTrack[this.selectedBookingUUID]) {
      const splitPaymentAmount =
        this.bookingsAmountsTrack[this.selectedBookingUUID]
          .splitPaymentAmount || 0;
      const splitAmountProvided =
        this.bookingsAmountsTrack[this.selectedBookingUUID]
          .splitAmountProvided || 0;
      const dt2 = new Date();
      if (this.logGettersPerformance) {
        console.log("splitAmount: ", (dt2.getTime() - dt1.getTime()) / 1000);
      }
      return { splitPaymentAmount, splitAmountProvided };
    }
    const dt2 = new Date();
    if (this.logGettersPerformance) {
      console.log("splitAmount: ", (dt2.getTime() - dt1.getTime()) / 1000);
    }
    return null;
  }

  public get getCalculatorValue(): string {
    return this.calculatorValue;
  }

  get selectedBooking(): IProductSlotBooking | null {
    return this.bookings[this.selectedBookingUUID] || null;
  }

  get bookingStep(): BookingStep {
    const step =
      this.selectedBooking?.step ??
      this.bookingsSteps[this.selectedBookingUUID];
    if (!step) {
      LogService.info(
        `L3-5844 BookingModule: No booking step found for UUID ${this.selectedBookingUUID}`,
        {
          selectedBookingUUID: this.selectedBookingUUID,
          bookingsSteps: this.bookingsSteps,
        },
        LogLevel.Error
      );

      // Fallback to Order step if no step is found
      return BookingStep.Order;
    }
    return step;
  }

  /**
   * Returns (sum of pss values if paymentStatus not completed OR sum of pss values and _discountValues if paymentStatus completed(in case of cash)
   * or has started (in case of cards))
   *
   */
  get pssSum() {
    let amount = 0;
    const pssList = BookingModule.selectedBooking?.pssList;

    if (!pssList) {
      return amount;
    }

    for (let i = 0; i < pssList.length; i++) {
      amount +=
        pssList[i].paymentStatus === PaymentStatus.Completed ||
        pssList[i].paymentStatus === PaymentStatus.Started
          ? getPSSValue(pssList[i]) + (pssList[i]._discountValue || 0)
          : getPSSValue(pssList[i]);
    }
    return amount;
  }

  /**
   * Returns paid amound of the booking
   */
  get totalPaid() {
    const booking = this.selectedBooking;
    let total = 0;

    if (!booking) {
      return total;
    }
    const completedAccountings = booking.accountings.filter(
      (acc) => acc.state === 5
    );
    for (const acc of completedAccountings) {
      for (const entry of acc.accountingEntriesList) {
        total += entry.value;
      }
    }

    return total;
  }

  /**
   * returns what was paid, and it considers the cash bills provided even if it was bigger than its accounting
   */
  public get amountPaid() {
    let oldCashSum = 0;
    // only consider completed accountings (state 5) that were paid in cash and come from backend (they have an id)
    const oldCashAccountings =
      BookingModule.selectedBooking?.accountings.filter(
        (acc) =>
          acc.state === 5 &&
          acc.paymentMethod === PaymentMethods.METHOD_POS_CASH &&
          acc.id
      );
    if (oldCashAccountings && oldCashAccountings.length) {
      const aeValues = oldCashAccountings
        ?.map((acc) => acc.accountingEntriesList)
        .flat()
        .map((ae) => ae.value);
      if (aeValues && aeValues.length) {
        oldCashSum = sumArray(aeValues);
      }
    }
    return (
      BookingModule.totalNonCashPaidAmount +
      oldCashSum +
      BookingModule.sumOfProvidedCashBills
    );
  }

  /**
   * Returns the current amount of the booking that is going to be paid
   */
  get currentAmount(): number {
    return this.bookingsAmountsTrack[this.selectedBookingUUID]
      ? this.bookingsAmountsTrack[this.selectedBookingUUID].currentAmount
      : 0;
  }

  /**
   * Returns the confirmed amounts of the booking that is going to be paid
   */
  get confirmedAmounts() {
    return this.bookingsAmountsTrack[this.selectedBookingUUID]
      ? this.bookingsAmountsTrack[this.selectedBookingUUID].confirmedAmounts
      : [];
  }

  /**
   * Returns sum of the confirmed amounts of the booking that is going to be paid
   */
  get confirmedAmountsTotal(): number {
    if (!this.bookingsAmountsTrack[this.selectedBookingUUID]) {
      return 0;
    }
    const confirmedAmounts = this.confirmedAmounts.map(
      (c) => c.confirmedAmount
    );
    let total = 0;
    for (let i = 0; i < confirmedAmounts.length; i++) {
      total = total + confirmedAmounts[i];
    }
    return total;
  }

  /**
   * Returns sum of the cash bills provided during the payment
   * in split payment we can have multiple cash payments
   */
  get sumOfProvidedCashBills(): number {
    const booking = this.selectedBooking;
    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule);
    }
    // only consider accountings that do not contain an id, meaning they were not pulled from backend
    // this avoids duplicating cash amounts when calculating the amount paid of a given booking
    const completedAccounting = booking.accountings
      .filter((acc) => acc.state == AccountingState.Completed && !acc.id)
      .map((acc) => {
        return {
          currency: acc.currency,
          providedCashBill: acc.providedCashBill,
          exchangeRate: acc.exchangeRate,
        };
      });
    let total = 0;
    for (let i = 0; i < completedAccounting.length; i++) {
      const currency = completedAccounting[i].currency;
      const cashBillValue = completedAccounting[i].providedCashBill || 0;
      const reverseExchangeRate =
        1 / (completedAccounting[i].exchangeRate || 1);
      let convertedCashBillValue = Math.round(
        cashBillValue * reverseExchangeRate
      );
      if (currency === Currencies.CHF) {
        convertedCashBillValue = roundToNearestMultipleOf5(
          convertedCashBillValue
        );
      }
      total = total + convertedCashBillValue;
    }
    return total;
  }

  /**
   * Returns the total non cash paid amount of the booking
   * This sums up all non cash completed accountings of transaction type payment or refund
   */
  get totalNonCashPaidAmount(): number {
    if (!this.selectedBooking) {
      return 0;
    }
    // PS: cash roundings are excluded from this getter, they are reported on
    // their own line rather than counted as a non-cash payment.
    const completedAccountings = this.selectedBooking.accountings.filter(
      (acc) =>
        acc.state === AccountingState.Completed &&
        acc.paymentMethod !== PaymentMethods.METHOD_POS_CASH &&
        acc.paymentMethod !== PaymentMethods.METHOD_CASH_ROUNDING
    );
    let total = 0;
    for (let i = 0; i < completedAccountings.length; i++) {
      const accounting = completedAccountings[i];
      if (
        accounting.transactionType ===
          TransactionTypes.TRANSACTION_TYPE_PAYMENT ||
        accounting.transactionType === TransactionTypes.TRANSACTION_TYPE_REFUND
      ) {
        for (let j = 0; j < accounting.accountingEntriesList.length; j++) {
          const entry = accounting.accountingEntriesList[j];
          total = total + entry.value;
        }
      }
    }
    return total;
  }

  /**
   * Total Belgian cash rounding booked against the selected booking, in cents.
   * Positive when the customer paid less than the exact total.
   */
  get roundingTotal(): number {
    if (!this.selectedBooking) {
      return 0;
    }
    const roundingAccountings = this.selectedBooking.accountings.filter(
      (acc) =>
        acc.state === AccountingState.Completed &&
        acc.paymentMethod === PaymentMethods.METHOD_CASH_ROUNDING &&
        (acc.transactionType === TransactionTypes.TRANSACTION_TYPE_PAYMENT ||
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_REFUND)
    );
    return sumArray(
      roundingAccountings.flatMap((acc) =>
        acc.accountingEntriesList.map((entry) => entry.value)
      )
    );
  }

  // /**
  //  * Returns sum of the confirmed amounts of the booking that is still not considered as a completed payment (completed accounting)
  //  */
  // get confirmedAmountWithoutCompletedAccounting(): number {
  //   if (!this.bookingsAmountsTrack[this.selectedBookingUUID]) {
  //     return 0;
  //   }
  //   const notPaidAccountings = this.selectedBooking?.accountings.filter(
  //     (acc) => acc.state !== AccountingState.Completed
  //   );
  //   const confirmedAmountsWithNotPaidAccountings = this.confirmedAmounts
  //     .filter((c) =>
  //       notPaidAccountings
  //         ?.map((acc) => acc.uuid)
  //         .includes(c.accountingInfo.accountingUUId)
  //     )
  //     .map((c) => c.confirmedAmount);
  //   let total = 0;
  //   for (let i = 0; i < confirmedAmountsWithNotPaidAccountings.length; i++) {
  //     total = total + confirmedAmountsWithNotPaidAccountings[i];
  //   }
  //   return total;
  // }

  /**
   * Returns selected payment method of the current booking
   */
  get selectedBookingPaymentMethod(): PaymentMethod | null {
    if (!this.selectedBooking) {
      return null;
    }

    const amountTrack = this.bookingsAmountsTrack[this.selectedBookingUUID];

    if (!amountTrack) {
      return null;
    }

    return amountTrack.paymentTool;
  }

  get selectedBookingPaymentMethodId(): string | null {
    if (!this.selectedBooking) {
      return null;
    }

    const amountTrack = this.bookingsAmountsTrack[this.selectedBookingUUID];

    if (!amountTrack || !amountTrack.paymentToolId) {
      return null;
    }

    return amountTrack.paymentToolId;
  }

  get selectedBookingPayment(): IBookingPayment | null {
    if (!this.selectedBooking) {
      return null;
    }

    const bookingPayment = this.bookingsAmountsTrack[this.selectedBookingUUID];

    if (!bookingPayment) {
      return null;
    }

    return bookingPayment;
  }

  get selectedBookingDiscountTrack(): IBookingDiscount | null {
    if (!this.selectedBooking) {
      return null;
    }

    const discountTrack = this.bookingsDiscountsTrack[this.selectedBookingUUID];

    if (!discountTrack) {
      return null;
    }

    return discountTrack;
  }

  // return current booking's adyen accountings and their adyen response status
  get selectedBookingAdyenReponseStatusTrack() {
    if (!this.selectedBooking) {
      return {};
    }
    const adyenResponseStatusTrack =
      this.adyenResponseStatusTrack[this.selectedBookingUUID];
    if (!adyenResponseStatusTrack) {
      return {};
    }
    return adyenResponseStatusTrack;
  }

  get bookingVisibleTabs() {
    return bookings.value.length > maxBookingTabsToShow
      ? bookings.value.slice(bookings.value.length - maxBookingTabsToShow)
      : bookings.value;
  }

  get bookingHiddenTabs() {
    return bookings.value.length > maxBookingTabsToShow
      ? bookings.value.slice(0, bookings.value.length - maxBookingTabsToShow)
      : [];
  }

  /**
   * Checks if the current order session has no accountingEntries linked to it
   * Absence of accountingEntries linked to an order session is a sign of no pss creation nor pss cancellation
   */
  get isCurrentOrderSessionEmpty() {
    const currentOrderSessionId = this.currentOrderSessionId;
    if (!currentOrderSessionId) {
      return true;
    }

    if (!this.selectedBooking) {
      return true;
    }

    if (this.selectedBooking.accountings.length === 0) {
      return true;
    }

    const accountingEntries = this.selectedBooking.accountings
      .map((acc) => acc.accountingEntriesList)
      .flat();
    if (accountingEntries.length === 0) {
      return true;
    }

    const orderSessionsIds: string[] = accountingEntries.map(
      (accEntry) => accEntry.orderSessionId
    );
    if (orderSessionsIds.length === 0) {
      return true;
    }

    return !orderSessionsIds.includes(currentOrderSessionId);
  }

  @Mutation
  linkWaiver(data: { bookingUUID: string; waiver: ISignedWaiver }) {
    const { bookingUUID, waiver } = data;
    if (!this.bookingsWaiversTrack[bookingUUID]) {
      this.bookingsWaiversTrack[bookingUUID] = { linkedWaivers: [] };
    }
    const alreadyLinked = this.bookingsWaiversTrack[
      bookingUUID
    ].linkedWaivers.some((w) => w.waiverId === waiver.waiverId);
    if (!alreadyLinked) {
      this.bookingsWaiversTrack[bookingUUID].linkedWaivers.push(waiver);
    }
  }

  @Mutation
  unlinkWaiver(data: { bookingUUID: string; waiverId: string }) {
    const { bookingUUID, waiverId } = data;
    if (!this.bookingsWaiversTrack[bookingUUID]) return;
    this.bookingsWaiversTrack[bookingUUID].linkedWaivers =
      this.bookingsWaiversTrack[bookingUUID].linkedWaivers.filter(
        (w) => w.waiverId !== waiverId
      );
  }

  @Mutation
  clearBookingWaivers(bookingUUID: string) {
    if (this.bookingsWaiversTrack[bookingUUID]) {
      delete this.bookingsWaiversTrack[bookingUUID];
    }
  }

  // @Mutation
  // updatedIsLoading(value: boolean) {
  //   this.isLoading = value;
  // }
  @Mutation
  setTabAnimation(isAnimated: boolean) {
    this.isTabAnimated = isAnimated;
  }

  @Mutation
  SET_CALC_VALUE(payload: string) {
    this.calculatorValue = payload;
  }

  @Mutation
  restore(bookingFields: Partial<IBookingState>) {
    const {
      bookings,
      bookingList,
      bookingsSteps,
      selectedBookingUUID,
      bookingsAmountsTrack,
      bookingsDiscountsTrack,
      promocodes,
      bookingsPendingSynchronization,
      adyenResponseStatusTrack,
      currentOrderSessionId,
      recentTabNames,
      currentBookingCount,
      bookingsWaiversTrack,
      bookingChanges,
    } = bookingFields;

    // keep a record of pending accountings that were marked as failed after a reload
    // it will be used to update adyenResponseStatusTrack when restoring it from local storage
    const adyenResponseStatusTrackUpdates: IAdyenResponseStatusTrack = {};

    if (bookings) {
      // after a reload, check and mark locally created pending adyen accountings as failed when restoring bookings
      // this is in order to prevent POS from confirming these accountings without a pspReference upon booking completion
      for (const uuid in bookings) {
        const booking = bookings[uuid];
        booking.accountings.forEach((accounting) => {
          if (
            accounting.paymentMethod === PaymentMethods.METHOD_POS_ADYEN &&
            accounting.state === AccountingState.Pending &&
            !accounting.id
          ) {
            LogService.info(
              `Pending adyen accounting found after a reload, marking it as failed. booking: ${booking.uuid}, accounting: ${accounting.uuid}`
            );
            accounting.state = AccountingState.Failed;
            if (booking.uuid && accounting.uuid) {
              if (!adyenResponseStatusTrackUpdates[booking.uuid]) {
                // initialize if not already
                adyenResponseStatusTrackUpdates[booking.uuid] = {};
              }
              adyenResponseStatusTrackUpdates[booking.uuid][accounting.uuid] =
                AdyenResponseStatus.Aborted;
            }
          }
        });
      }
      this.bookings = bookings;
    }
    if (bookingList) {
      this.bookingList = bookingList;
    }
    if (bookingsSteps) {
      this.bookingsSteps = bookingsSteps;
    }
    if (selectedBookingUUID) {
      this.selectedBookingUUID = selectedBookingUUID;
    }
    if (bookingsAmountsTrack) {
      this.bookingsAmountsTrack = bookingsAmountsTrack;
    }

    if (bookingsDiscountsTrack) {
      this.bookingsDiscountsTrack = bookingsDiscountsTrack;
    }

    if (promocodes) {
      this.promocodes = promocodes;
    }

    if (bookingsPendingSynchronization) {
      if (Array.isArray(bookingsPendingSynchronization)) {
        // migration after changing the type from string[] to IBookingsPendingSync
        LogService.info("WARNING: unsynced bookings array not migrated", {
          bookingsPendingSynchronization,
        });
        this.bookingsPendingSynchronization = {};
      } else {
        this.bookingsPendingSynchronization = bookingsPendingSynchronization;
      }
    }

    if (adyenResponseStatusTrack) {
      // we update the adyen response status track according to the object created before restoring bookings
      for (const bookingUuid in adyenResponseStatusTrackUpdates) {
        const accountings = adyenResponseStatusTrackUpdates[bookingUuid];
        for (const accountingUuid in accountings) {
          const response = accountings[accountingUuid];
          // safety check in case of undefined properties
          if (
            adyenResponseStatusTrack[bookingUuid] &&
            adyenResponseStatusTrack[bookingUuid][accountingUuid]
          )
            adyenResponseStatusTrack[bookingUuid][accountingUuid] = response;
        }
      }
      this.adyenResponseStatusTrack = adyenResponseStatusTrack;
    }

    if (currentOrderSessionId) {
      this.currentOrderSessionId = currentOrderSessionId;
    }

    if (recentTabNames) {
      this.recentTabNames = recentTabNames;
    }

    if (currentBookingCount) {
      this.currentBookingCount = currentBookingCount;
    }

    if (bookingsWaiversTrack) {
      this.bookingsWaiversTrack = bookingsWaiversTrack;
    }

    if (bookingChanges) {
      this.bookingChanges = bookingChanges;
    }
  }

  @Mutation
  setBookingStep(payload: { bookingUUID: string; step: BookingStep }) {
    const { bookingUUID, step } = payload;

    const booking = this.bookings[bookingUUID];
    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule);
    }

    LogService.info(
      `BookingModule: Setting step '${step}' on booking ${bookingUUID}`
    );
    this.bookings[bookingUUID].step = step;
  }

  @Mutation
  removeBookingStep(bookingUUID: string) {
    if (this.bookingsSteps[bookingUUID]) {
      delete this.bookingsSteps[bookingUUID];
    }
  }

  @Mutation
  addBooking(booking: IProductSlotBooking) {
    const uuid = booking.uuid as string;
    const reference = booking.reference as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }

    this.bookings[uuid] = booking;
    this.bookingList.push({ uuid: uuid, reference: reference });
  }

  @Mutation
  removeBookingFromList(booking: IProductSlotBooking) {
    const uuid = booking.uuid;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }
    delete this.bookings[uuid];
    // pull(this.bookingList, uuid);
    this.bookingList = this.bookingList.filter((el) => el.uuid !== uuid);
  }

  @Mutation
  selectBookingMutation(payload: {
    bookingUUID: string;
    currentOrderSessionId: string;
    suspendBookingUUID?: string;
  }) {
    const { bookingUUID, currentOrderSessionId, suspendBookingUUID } = payload;

    if (suspendBookingUUID) {
      this.bookings[suspendBookingUUID].isSuspended = true;
    }

    this.selectedBookingUUID = bookingUUID;
    this.currentOrderSessionId = currentOrderSessionId;
  }

  @Mutation
  storeBookingInPendingSyncBookings(data: {
    bookingUUID: string;
    type: SyncType;
  }) {
    const { bookingUUID, type } = data;

    if (!bookingUUID) {
      throw new Error(PSBErrors.NoSelectedBookingInModule);
    }

    // Initialize array if it doesn't exist
    this.bookingsPendingSynchronization[bookingUUID] ??= [];

    // Add type only if it doesn't already exist
    if (!this.bookingsPendingSynchronization[bookingUUID].includes(type)) {
      this.bookingsPendingSynchronization[bookingUUID].push(type);
    }
  }

  @Mutation
  removeSyncedBookingFromPendingSyncBookings(data: {
    synchedBooking: string[];
    typeToDelete?: SyncType;
  }) {
    const { synchedBooking, typeToDelete } = data;
    synchedBooking.forEach((uuid) => {
      if (!this.bookingsPendingSynchronization[uuid]) return; // Skip if UUID doesn't exist

      // Filter out only typeToDelete from the array
      this.bookingsPendingSynchronization[uuid] =
        this.bookingsPendingSynchronization[uuid].filter(
          (type) => type !== typeToDelete
        );

      // If no types are left, remove the UUID entry
      if (this.bookingsPendingSynchronization[uuid].length === 0) {
        delete this.bookingsPendingSynchronization[uuid];
      }
    });
  }

  @Mutation
  removePSS(data: { bookingUUID: string; criteria: IRemovePSS }) {
    const { bookingUUID, criteria } = data;
    const booking = this.bookings[bookingUUID];
    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule);
    }

    // removePSSFromPSB(criteria, booking);
  }

  @Mutation
  setCustomerData(data: IBookingCustomerInfo) {
    LogService.info(`Assigning customer to booking: ${data.bookingUuid}`, {
      data,
    });
    const id = data.id;
    const uuid = data.bookingUuid;
    const email = data.email;
    const lastName = data.lastName;
    const firstName = data.firstName;
    this.bookings[uuid].customerId = id;
    this.bookings[uuid].email = email;
    this.bookings[uuid].lastName = lastName;
    this.bookings[uuid].firstName = firstName;
  }

  @Mutation
  updateBooking(booking: IProductSlotBooking) {
    const uuid = booking.uuid as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }
    this.bookings[uuid] = booking;
  }

  @Mutation
  addPaymentTracking(data: { BookingUUID: string; track: IBookingPayment }) {
    const { BookingUUID, track } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }
    this.bookingsAmountsTrack[uuid] = track;
  }

  @Mutation
  removeAmountTracking(bookingUUID: string) {
    if (this.bookingsAmountsTrack[bookingUUID]) {
      delete this.bookingsAmountsTrack[bookingUUID];
    }
  }

  @Mutation
  setPaymentCurrency(data: { BookingUUID: string; currency: Currencies }) {
    const { BookingUUID, currency } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }

    this.bookingsAmountsTrack[uuid].currency = currency;
  }

  // @Mutation
  // setPaymentTool(data: {
  //   BookingUUID: string;
  //   // paymentTool: PaymentTool;
  //   paymentToolId: PaymentMethodTypes;
  // }) {
  //   const { BookingUUID, /*paymentTool,*/ paymentToolId } = data;
  //   const uuid = BookingUUID as string;
  //   if (!uuid) {
  //     throw new Error(PSBErrors.HasNoUUID);
  //   }

  //   this.bookingsAmountsTrack[uuid].paymentTool =
  //     paymentMethodToOrderPaymentMethod(paymentToolId);
  //   this.bookingsAmountsTrack[uuid].paymentToolId = paymentToolId;
  // }

  @Mutation
  setPaymentCard(data: { BookingUUID: string; paymentCard: PaymentCard }) {
    const { BookingUUID, paymentCard } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }

    this.bookingsAmountsTrack[uuid].paymentCard = paymentCard;
  }

  @Mutation
  addCurrentAmount(data: { BookingUUID: string; amount: number }) {
    const { BookingUUID, amount } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }

    this.bookingsAmountsTrack[uuid].currentAmount = amount;
  }

  @Mutation
  updateCurrentAmount(data: { BookingUUID: string; amount: number }) {
    const { BookingUUID, amount } = data;

    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }
    this.bookingsAmountsTrack[uuid].currentAmount = amount;
  }

  @Mutation
  confirmCurrentAmount(data: {
    BookingUUID: string;
    accountingInfo: IAccountingInfo;
  }) {
    // Takes the current amount and push it to confirmed amounts array along with accountingInfo

    const { BookingUUID, accountingInfo } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }

    const confirmedAmount: IConfirmedAmount = {
      id: generateID(),
      confirmedAmount: this.bookingsAmountsTrack[uuid].currentAmount,
      accountingInfo: accountingInfo,
    };

    this.bookingsAmountsTrack[uuid].confirmedAmounts.push(confirmedAmount);
    this.bookingsAmountsTrack[uuid].currentAmount = 0;
  }

  @Mutation
  updateConfirmedAmounts(data: {
    bookingUUID: string;
    confirmedAmounts: IConfirmedAmount[];
  }) {
    const { bookingUUID, confirmedAmounts } = data;
    const amountTrack = this.bookingsAmountsTrack[bookingUUID];

    if (!amountTrack) {
      throw new Error(`Amount track not found for uuid ${bookingUUID}`);
    }

    amountTrack.confirmedAmounts = confirmedAmounts;
  }

  @Mutation
  setSplitPaymentAmount(data: {
    BookingUUID: string;
    splitAmount: number;
    splitAmountProvided?: number;
  }) {
    const { BookingUUID, splitAmount } = data;
    const splitAmountProvided = data.splitAmountProvided || splitAmount;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }

    this.bookingsAmountsTrack[uuid].splitPaymentAmount = splitAmount;

    this.bookingsAmountsTrack[uuid].splitAmountProvided = splitAmountProvided;
  }

  @Mutation
  removeSplitPaymentAmount(BookingUUID: string) {
    if (!BookingUUID) {
      throw new Error(PSBErrors.HasNoUUID);
    }

    if (this.bookingsAmountsTrack[BookingUUID].splitPaymentAmount) {
      delete this.bookingsAmountsTrack[BookingUUID].splitPaymentAmount;
    }
  }

  @Mutation
  updateBookingAccountings(data: {
    bookingUUID: string;
    accountings: IAccounting[];
  }) {
    const { bookingUUID, accountings } = data;
    const booking = this.bookings[bookingUUID];

    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule + ` - uuid = ${bookingUUID}`);
    }

    booking.accountings = accountings;
  }

  @Mutation
  deleteConfirmedAmount(id: string) {
    const uuid = this.selectedBookingUUID;
    const index = this.bookingsAmountsTrack[uuid].confirmedAmounts.findIndex(
      (c) => c.id === id
    );
    if (index > -1) {
      this.bookingsAmountsTrack[uuid].confirmedAmounts.splice(index, 1);
    }
  }

  @Mutation
  emptyConfirmedAmountsArray(bookingUUID: string) {
    this.bookingsAmountsTrack[bookingUUID].confirmedAmounts = [];
  }

  @Mutation
  setSelectedCashCurrency(currency: Currency | null) {
    this.selectedCashCurrency = currency;
  }

  @Mutation
  setAdyenResponseStatusTrack(data: {
    bookingUuid: string;
    accountingUuid: string;
    status: AdyenResponseStatus;
  }) {
    if (!this.adyenResponseStatusTrack[data.bookingUuid]) {
      this.adyenResponseStatusTrack[data.bookingUuid] = {};
    }
    this.adyenResponseStatusTrack[data.bookingUuid][data.accountingUuid] =
      data.status;
  }

  @Mutation
  removeAdyenResponseStatusTrack(bookingUuid: string) {
    if (this.adyenResponseStatusTrack[bookingUuid]) {
      delete this.adyenResponseStatusTrack[bookingUuid];
    }
  }

  @Mutation
  setCurrentOrderSessionId(orderSessionId: string) {
    this.currentOrderSessionId = orderSessionId;
  }

  @Mutation
  updateOrderSession(data: {
    bookingUuid: string;
    orderSessionId: string;
    newOrderSession: BaseOrderSession;
  }) {
    const { bookingUuid, orderSessionId, newOrderSession } = data;
    const booking = this.bookings[bookingUuid];
    if (!booking) {
      LogService.info("Booking not found", { bookingUuid });
      return;
    }

    const index = booking.orderSessions.findIndex(
      (orderSession) => orderSession.id === orderSessionId
    );

    if (index === -1) {
      LogService.info("Order session not found", { orderSessionId });
      return;
    }

    // Replace and update
    booking.orderSessions[index] = {
      ...newOrderSession,
      updatedAt: dayjs().toISOString(),
    };
  }

  @Mutation
  completeOrderSession(data: {
    bookingUuid: string;
    orderSessionId: string;
    state: OrderSessionState;
  }) {
    const { bookingUuid, orderSessionId, state } = data;
    const booking = this.bookings[bookingUuid];
    if (!booking) {
      LogService.info("Booking not found", { bookingUuid });
      return;
    }
    const orderSession = booking.orderSessions.find(
      (orderSession) => orderSession.id === orderSessionId
    );
    if (!orderSession) {
      LogService.info("Order session not found", { orderSessionId });
      return;
    }
    if (orderSession.state === state) {
      LogService.info("Order session already completed", { state });
    }
    const completionTime = dayjs().toISOString();
    orderSession.state = state;
    orderSession.completionDate = completionTime;
    orderSession.sessionEnd = completionTime;
    orderSession.updatedAt = completionTime;
  }

  @Mutation
  addOrderSessionToBooking(data: {
    orderSession: BaseOrderSession;
    bookingUuid: string;
  }) {
    const { orderSession, bookingUuid } = data;
    const booking = this.bookings[bookingUuid];
    if (!booking) {
      LogService.info("Booking not found", { bookingUuid });
      return;
    }
    booking.orderSessions.push(orderSession);
    this.currentOrderSessionId = orderSession.id;
  }

  @Mutation
  setOrderSessionHasOrderLines(data: {
    bookingUuid: string;
    orderSessionId: string;
    hasOrderLines: boolean;
  }) {
    const { bookingUuid, orderSessionId, hasOrderLines } = data;
    const booking = this.bookings[bookingUuid];
    if (!booking) {
      LogService.info("Booking not found", { bookingUuid });
      return;
    }
    const orderSession = booking.orderSessions.find(
      (orderSession) => orderSession.id === orderSessionId
    );
    if (!orderSession) {
      LogService.info("Order session not found", { orderSessionId });
      return;
    }
    orderSession.hasOrderLines = hasOrderLines;
  }

  @Mutation
  setBookingTabName(name: string) {
    const booking = this.bookings[this.selectedBookingUUID];
    if (!booking) {
      return;
    }
    booking.tabName = name;
  }

  @Mutation
  addRecentTabName(name: string) {
    const maxTabNames = 4;
    // do nothing if the name already exists in the array
    if (this.recentTabNames.includes(name)) {
      return;
    }
    // follows a LIFO behavior
    this.recentTabNames.unshift(name);
    if (this.recentTabNames.length > maxTabNames) {
      this.recentTabNames = this.recentTabNames.slice(
        0,
        this.recentTabNames.length - 1
      );
    }
  }

  @Mutation
  increaseBookingCount() {
    this.currentBookingCount++;
  }

  @Mutation
  resetBookingCount() {
    this.currentBookingCount = 0;
  }

  // Clear all bookings from module
  @Mutation
  clearAllBookingsData() {
    // clear all bookings and their related data
    this.bookings = {};
    this.bookingList = [];
    this.bookingsSteps = {};
    this.bookingsAmountsTrack = {};
    this.bookingsDiscountsTrack = {};
    this.promocodes = [];
    this.adyenResponseStatusTrack = {};
    this.bookingsWaiversTrack = {};
  }

  // Clear all bookings from module
  @Action({ rawError: true })
  public async clearAllBookings() {
    // clear all bookings and their related data
    this.clearAllBookingsData();
  }

  @Action({ commit: "SET_CALC_VALUE", rawError: true })
  public setCalcValue(calcValue: string) {
    return calcValue;
  }

  @Action
  async selectBooking(bookingUUID: string) {
    const booking = this.bookings[bookingUUID];

    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule);
    }

    let suspendBookingUUID: string | undefined;
    const previousUUID = this.selectedBookingUUID;

    if (
      previousUUID &&
      this.bookings[previousUUID] &&
      this.currentOrderSessionId
    ) {
      const previousBooking = this.bookings[previousUUID];
      const orderSession = previousBooking.orderSessions.find(
        (session) => session.id === this.currentOrderSessionId
      );

      const isIncomplete =
        orderSession &&
        !orderSession.signatureInfoFR &&
        orderSession.state !== OrderSessionState.COMPLETED &&
        this.bookingsSteps[previousUUID] !== BookingStep.CompleteOrder;

      if (isIncomplete) {
        LogService.info(
          `Booking ${previousUUID} is not signed nor completed yet, setting it to suspended`
        );
        suspendBookingUUID = previousUUID;
      }
    }

    const sortedSessions = booking.orderSessions.sort(
      (a, b) =>
        dayjs(b.sessionStart).valueOf() - dayjs(a.sessionStart).valueOf()
    );
    const newestSession = sortedSessions[0];

    // Commit state update
    this.context.commit("selectBookingMutation", {
      bookingUUID,
      currentOrderSessionId: newestSession.id,
      suspendBookingUUID,
    });

    // Handle side-effects after mutation

    // France only: suspend the previous receipt when switching away
    if (suspendBookingUUID) {
      try {
        await processAdditionalEvents(EventType.SUSPEND_RECEIPT);
      } catch (err) {
        LogService.info(
          `Error while processing suspend receipt event for booking ${suspendBookingUUID}`,
          { err }
        );
      }
    }

    // France only: resume a suspended receipt when switching back
    if (
      booking.isSuspended &&
      this.bookingsSteps[bookingUUID] !== BookingStep.CompleteOrder
    ) {
      LogService.info(
        `Newly selected booking ${bookingUUID} is suspended, setting it to resumed`
      );
      try {
        await processAdditionalEvents(EventType.RESUME_RECEIPT);
      } catch (err) {
        LogService.info(
          `Error while processing resume receipt event for booking ${bookingUUID}`,
          { err }
        );
      }
    }

    // Germany only: start a new sale event for the selected booking
    const currentOrderSession = booking.orderSessions
      .slice()
      .sort(
        (a, b) =>
          dayjs(b.sessionStart).valueOf() - dayjs(a.sessionStart).valueOf()
      )
      .find((session) => session.state === OrderSessionState.PENDING);
    if (currentOrderSession && bookingUUID === this.selectedBookingUUID) {
      // processStartSaleEvent(booking, currentOrderSession.id).catch((error) => {
      //   LogService.info("Error processing start sale event", { error });
      // });
    }
  }
}

const BookingModule = getModule(BookingStoreModule);
export default BookingModule;
